import { useEffect, useMemo, useState, type InputHTMLAttributes } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@soroush/ui';
import {
  passengerTypeOn,
  validatePassengerMix,
  type PassengerMixError,
  type PassengerType,
} from '@soroush/shared/passengers';
import { apiFetch } from '../lib/api';
import type { ClubHome } from '../lib/club';
import { bookingErrorText } from '../lib/errors';
import { isInternationalRoute } from '../lib/airports';
import { formatIsoDate, formatMoney, tehranIsoDate } from '../lib/format';
import { isoToJalali, todayIso, toFaDigits } from '../lib/jalali';
import {
  isIsoDate,
  isLatinName,
  isValidMobile,
  isValidNationalCode,
  normalizeMobile,
  toLatinDigits,
} from '../lib/validation';
import { BookingSteps } from '../components/BookingSteps';
import { FlightSummaryCard, type FlightSummary } from '../components/FlightSummaryCard';
import { JalaliDateSelect } from '../components/JalaliDateSelect';
import { departsAfter, saveRoundTripHolds } from '../lib/roundTrip';

type BookFlight = FlightSummary & {
  flightId: string;
  arrivalAt?: string | null;
  totalFare: number;
  baseFare?: number | null;
  taxes?: number | null;
  currency: string;
  source?: 'nrs' | 'local';
  fareClass?: string;
  refundable?: boolean | null;
};

interface FareLine {
  fare: number;
  tax: number;
  total: number;
}

interface FareQuote {
  adult: FareLine;
  child: FareLine | null;
  infant: FareLine | null;
  baggage: { weight: string | null; pieces: string | null };
  refundRules: Array<{ window: string; mode: string; value: number }>;
}

const MIX_MESSAGES: Record<PassengerMixError, string> = {
  NO_ADULT: 'common.mixNoAdult',
  TOO_MANY_INFANTS: 'common.mixTooManyInfants',
  TOO_MANY_DEPENDANTS: 'common.mixTooManyDependants',
};

const TYPE_LABELS: Record<PassengerType, string> = {
  ADT: 'common.adult',
  CHD: 'common.child',
  INF: 'common.infant',
};

type PassengerForm = {
  firstName: string;
  lastName: string;
  gender: '' | 'M' | 'F';
  birthDate: string;
  nationality: 'IR' | 'OTHER';
  nationalCode: string;
  passportNumber: string;
  passportExpiry: string;
  passportCountry: string;
};

const emptyPassenger: PassengerForm = {
  firstName: '',
  lastName: '',
  gender: '',
  birthDate: '',
  nationality: 'IR',
  nationalCode: '',
  passportNumber: '',
  passportExpiry: '',
  passportCountry: '',
};

type Errors = Record<string, string>;

function Field({
  id,
  label,
  error,
  ...props
}: { id: string; label: string; error?: string } & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div className={`sa-field${error ? ' has-error' : ''}`}>
      <label className="sa-label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="sa-input"
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
        {...props}
      />
      {error ? (
        <p className="field-error" id={`${id}-error`}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

function useLegQuote(flight?: BookFlight) {
  const liveFlightId = flight?.source === 'nrs' ? flight.flightId : undefined;
  const liveFareClass = flight?.source === 'nrs' ? flight.fareClass : undefined;
  const [quote, setQuote] = useState<FareQuote | null>(null);
  const [quoteState, setQuoteState] = useState<'idle' | 'loading' | 'error'>('idle');

  useEffect(() => {
    if (!liveFlightId || !liveFareClass) {
      setQuote(null);
      setQuoteState('idle');
      return;
    }
    let cancelled = false;
    setQuoteState('loading');
    const qs = new URLSearchParams({ flightId: liveFlightId, fareClass: liveFareClass });
    void apiFetch<{ quote: FareQuote }>(`/api/v1/search/fare?${qs}`)
      .then((data) => {
        if (cancelled) return;
        setQuote(data.quote);
        setQuoteState('idle');
      })
      .catch(() => {
        if (!cancelled) setQuoteState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [liveFlightId, liveFareClass]);

  return { quote, quoteState };
}

export function BookPage() {
  const { t, i18n } = useTranslation();
  const isFa = i18n.language.startsWith('fa');
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as
    | {
        flight?: BookFlight;
        returnFlight?: BookFlight;
        extraFlights?: BookFlight[];
        passengers?: number;
      }
    | null;
  const flight = state?.flight;
  const returnFlight = state?.returnFlight;
  const extraFlights = state?.extraFlights ?? [];
  const paxCount = Math.min(Math.max(state?.passengers ?? 1, 1), 9);

  const [phase, setPhase] = useState<'passengers' | 'services' | 'review'>('passengers');
  const [passengers, setPassengers] = useState<PassengerForm[]>(() =>
    Array.from({ length: paxCount }, () => ({ ...emptyPassenger })),
  );
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [priceChange, setPriceChange] = useState<{
    bookingId: string;
    returnBookingId?: string;
    amount: number;
    outboundAmount?: number;
  } | null>(null);

  useEffect(() => {
    if (!localStorage.getItem('sa_access_token')) return;
    let cancelled = false;
    void apiFetch<ClubHome>('/api/v1/ffp/me')
      .then((data) => {
        if (cancelled) return;
        const profile = data.profile;
        setPassengers((current) => {
          const first = current[0];
          if (!first || first.firstName.trim()) return current;
          const foreign = Boolean(profile.nationality && profile.nationality !== 'IR');
          const next = [...current];
          next[0] = {
            ...first,
            firstName: profile.firstName ?? '',
            lastName: profile.lastName ?? '',
            gender: profile.gender ?? '',
            birthDate: profile.birthDate ?? '',
            nationality: foreign ? 'OTHER' : 'IR',
            nationalCode: profile.nationalId ?? '',
            passportNumber: profile.passportNumber ?? '',
            passportExpiry: profile.passportExpiry ?? '',
            passportCountry: profile.passportCountry || (foreign ? profile.nationality ?? '' : ''),
          };
          return next;
        });
        if (data.contact.phone) setMobile((current) => current || data.contact.phone || '');
        if (data.contact.email) setEmail((current) => current || data.contact.email || '');
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const outboundQuote = useLegQuote(flight);
  const returnQuote = useLegQuote(returnFlight);

  const currentJy = useMemo(() => isoToJalali(todayIso())?.jy ?? 1405, []);
  const birthYears = useMemo(
    () => Array.from({ length: 100 }, (_, i) => currentJy - i),
    [currentJy],
  );
  const expiryYears = useMemo(
    () => Array.from({ length: 13 }, (_, i) => currentJy + i),
    [currentJy],
  );

  if (!flight) {
    return (
      <main className="sa-page">
        <div className="sa-container">
          <div className="sa-alert sa-alert--error">{t('common.noResults')}</div>
          <Button type="button" onClick={() => navigate('/search')}>
            {t('nav.items.buyTicket')}
          </Button>
        </div>
      </main>
    );
  }

  const bookedFlight = flight;
  const returnLegFlight = returnFlight;
  const international =
    isInternationalRoute(bookedFlight.origin, bookedFlight.destination) ||
    (returnLegFlight
      ? isInternationalRoute(returnLegFlight.origin, returnLegFlight.destination)
      : false);
  const departureIso = tehranIsoDate(bookedFlight.departureAt);
  const returnIso = returnLegFlight ? tehranIsoDate(returnLegFlight.departureAt) : '';
  const travelEndIso = returnIso || departureIso;
  const money = (amount: number) => formatMoney(amount, bookedFlight.currency, isFa);
  const num = (n: number) => (isFa ? toFaDigits(n) : String(n));
  const returnInvalid = returnLegFlight
    ? !departsAfter(
        returnLegFlight.departureAt,
        bookedFlight.arrivalAt ?? bookedFlight.departureAt,
      )
    : false;
  const currencyMismatch = returnLegFlight
    ? returnLegFlight.currency !== bookedFlight.currency
    : false;

  function priceLeg(
    target: BookFlight,
    quoted: FareQuote | null,
    quoteState: 'idle' | 'loading' | 'error',
  ) {
    const iso = tehranIsoDate(target.departureAt);
    const loading = target.source === 'nrs' && Boolean(target.fareClass) && quoteState === 'loading';
    const failed = target.source === 'nrs' && Boolean(target.fareClass) && quoteState === 'error';
    const legTypes = passengers.map((p) =>
      isIsoDate(p.birthDate) ? (passengerTypeOn(p.birthDate, iso) ?? 'ADT') : 'ADT',
    );
    function unitFor(type: PassengerType): FareLine | null {
      if (!quoted) return { fare: target.totalFare, tax: 0, total: target.totalFare };
      if (type === 'CHD') return quoted.child;
      if (type === 'INF') return quoted.infant;
      return quoted.adult;
    }
    const lines = (['ADT', 'CHD', 'INF'] as const)
      .map((type) => ({
        type,
        count: legTypes.filter((item) => item === type).length,
        unit: unitFor(type),
      }))
      .filter((line) => line.count > 0);
    const ready = !loading && !failed && lines.every((line) => line.unit !== null);
    const legTotal = lines.reduce((sum, line) => sum + (line.unit?.total ?? 0) * line.count, 0);
    const tax = quoted
      ? lines.reduce((sum, line) => sum + (line.unit?.tax ?? 0) * line.count, 0)
      : (target.taxes ?? 0) * paxCount;
    return { flight: target, lines, ready, total: legTotal, tax, loading, failed, quote: quoted };
  }

  const outboundLeg = priceLeg(bookedFlight, outboundQuote.quote, outboundQuote.quoteState);
  const inboundLeg = returnLegFlight
    ? priceLeg(returnLegFlight, returnQuote.quote, returnQuote.quoteState)
    : null;
  const priced =
    outboundLeg.ready && (!inboundLeg || inboundLeg.ready) && !returnInvalid && !currencyMismatch;
  const total = outboundLeg.total + (inboundLeg?.total ?? 0);
  const quoteLoading = outboundLeg.loading || Boolean(inboundLeg?.loading);
  const quoteFailed = outboundLeg.failed || Boolean(inboundLeg?.failed);

  const typeOf = (p: PassengerForm): PassengerType | null =>
    isIsoDate(p.birthDate) ? passengerTypeOn(p.birthDate, departureIso) : null;

  function clearError(key: string) {
    setErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function updatePassenger<K extends keyof PassengerForm>(index: number, key: K, value: PassengerForm[K]) {
    setPassengers((prev) => prev.map((p, i) => (i === index ? { ...p, [key]: value } : p)));
    clearError(`p${index}.${String(key)}`);
  }

  function needsPassport(p: PassengerForm) {
    return international || p.nationality === 'OTHER';
  }

  function validate(): Errors {
    const next: Errors = {};
    const required = t('common.requiredField');
    passengers.forEach((p, i) => {
      const key = (field: string) => `p${i}.${field}`;
      if (!p.firstName.trim()) next[key('firstName')] = required;
      else if (!isLatinName(p.firstName)) next[key('firstName')] = t('common.latinOnly');
      if (!p.lastName.trim()) next[key('lastName')] = required;
      else if (!isLatinName(p.lastName)) next[key('lastName')] = t('common.latinOnly');
      if (!p.gender) next[key('gender')] = required;
      if (!isIsoDate(p.birthDate) || p.birthDate >= todayIso()) next[key('birthDate')] = required;
      if (p.nationality === 'IR' && !isValidNationalCode(p.nationalCode)) {
        next[key('nationalCode')] = t('common.invalidNationalCode');
      }
      if (needsPassport(p)) {
        if (p.passportNumber.trim().length < 5) next[key('passportNumber')] = required;
        if (!isIsoDate(p.passportExpiry) || p.passportExpiry <= travelEndIso) {
          next[key('passportExpiry')] = required;
        }
      }
      if (p.nationality === 'OTHER' && !/^[A-Za-z]{2}$/.test(p.passportCountry.trim())) {
        next[key('passportCountry')] = t('common.invalidCountry');
      }
    });
    if (!isValidMobile(mobile)) next.mobile = t('common.invalidMobile');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) next.email = required;
    return next;
  }

  function onContinue(e: React.FormEvent) {
    e.preventDefault();
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0) {
      setError(t('common.fixErrors'));
      return;
    }
    const typesOn = (iso: string) =>
      passengers.map((p) => (isIsoDate(p.birthDate) ? (passengerTypeOn(p.birthDate, iso) ?? 'ADT') : 'ADT'));
    const mixError =
      validatePassengerMix(typesOn(departureIso)) ??
      (returnIso ? validatePassengerMix(typesOn(returnIso)) : null);
    if (mixError) {
      setError(t(MIX_MESSAGES[mixError]));
      return;
    }
    if (returnInvalid) {
      setError(t('common.returnTooEarly'));
      return;
    }
    if (!priced) {
      setError(t('common.fareUnavailable'));
      return;
    }
    setError('');
    setPhase('services');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function continueFromServices() {
    if (!priced) {
      setError(t('common.fareUnavailable'));
      return;
    }
    setError('');
    setPhase('review');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function documentFor(p: PassengerForm) {
    return needsPassport(p)
      ? p.passportNumber.trim().toUpperCase()
      : toLatinDigits(p.nationalCode).trim();
  }

  async function startPayment(bookingId: string) {
    const pay = await apiFetch<{ redirectUrl: string }>('/api/v1/payments/initiate', {
      method: 'POST',
      body: JSON.stringify({ bookingId }),
    });
    window.location.href = pay.redirectUrl;
  }

  async function createHold(target: BookFlight) {
    return apiFetch<{ booking: { _id: string; totalAmount: number } }>('/api/v1/bookings', {
      method: 'POST',
      body: JSON.stringify({
        flightId: target.flightId,
        fareClass: target.fareClass,
        cabin: target.cabin,
        contactEmail: email.trim(),
        contactPhone: normalizeMobile(mobile),
        passengers: passengers.map((p) => ({
          firstName: p.firstName.trim().toUpperCase(),
          lastName: p.lastName.trim().toUpperCase(),
          gender: p.gender,
          birthDate: p.birthDate,
          nationality: p.nationality === 'IR' ? 'IR' : p.passportCountry.trim().toUpperCase(),
          documentType: needsPassport(p) ? 'PASSPORT' : 'NATIONAL_ID',
          documentNumber: documentFor(p),
          documentExpiry: needsPassport(p) ? p.passportExpiry : undefined,
        })),
      }),
    });
  }

  async function releaseHold(bookingId: string) {
    try {
      await apiFetch(`/api/v1/bookings/${encodeURIComponent(bookingId)}/cancel`, { method: 'POST' });
    } catch {
      // The hold expires on its own if the airline release fails.
    }
  }

  async function rememberPair(outboundId: string, returnId: string) {
    saveRoundTripHolds({ outboundId, returnId });
    try {
      await apiFetch('/api/v1/bookings/round-trip', {
        method: 'POST',
        body: JSON.stringify({ outboundId, returnId }),
      });
    } catch {
      // Session storage still ties the two holds in this browser.
    }
  }

  async function onPay() {
    setSubmitting(true);
    setError('');
    if (extraFlights.length) {
      sessionStorage.setItem('sa_pending_flights', JSON.stringify(extraFlights));
    } else {
      sessionStorage.removeItem('sa_pending_flights');
    }
    try {
      if (priceChange) {
        if (priceChange.returnBookingId) {
          await rememberPair(priceChange.bookingId, priceChange.returnBookingId);
        }
        await startPayment(priceChange.bookingId);
        return;
      }
      const created = await createHold(bookedFlight);
      if (!returnLegFlight) {
        // The airline prices the PNR itself; never charge a different amount than was shown.
        if (created.booking.totalAmount !== total) {
          setPriceChange({ bookingId: created.booking._id, amount: created.booking.totalAmount });
          setSubmitting(false);
          return;
        }
        await startPayment(created.booking._id);
        return;
      }

      let createdReturn: { booking: { _id: string; totalAmount: number } };
      try {
        createdReturn = await createHold(returnLegFlight);
      } catch (err) {
        await releaseHold(created.booking._id);
        throw err;
      }
      const charged = created.booking.totalAmount + createdReturn.booking.totalAmount;
      await rememberPair(created.booking._id, createdReturn.booking._id);
      if (charged !== total) {
        setPriceChange({
          bookingId: created.booking._id,
          returnBookingId: createdReturn.booking._id,
          amount: charged,
          outboundAmount: created.booking.totalAmount,
        });
        setSubmitting(false);
        return;
      }
      await startPayment(created.booking._id);
    } catch (err) {
      setError(bookingErrorText(err, t));
      setSubmitting(false);
    }
  }

  function backToPassengers() {
    setPriceChange(null);
    setPhase('passengers');
  }

  function backToServices() {
    setPriceChange(null);
    setPhase('services');
  }

  const pricedLegs = inboundLeg ? [outboundLeg, inboundLeg] : [outboundLeg];
  const taxTotal = pricedLegs.reduce((sum, leg) => sum + leg.tax, 0);

  const priceAside = (
    <section className="booking-panel" aria-labelledby="price-title">
      <h2 id="price-title" className="booking-panel__title">
        {t('common.priceDetails')}
      </h2>
      {quoteLoading ? <p className="sa-text-muted">{t('common.fareChecking')}</p> : null}
      {quoteFailed || returnInvalid || currencyMismatch ? (
        <p className="field-error" role="alert">
          {returnInvalid ? t('common.returnTooEarly') : t('common.fareUnavailable')}
        </p>
      ) : null}
      {pricedLegs.map((leg) => (
        <div key={leg.flight.flightId}>
          {returnLegFlight ? (
            <h3 className="trip-price__leg">
              {leg.flight.flightId === returnLegFlight.flightId
                ? t('common.returnFlight')
                : t('common.outboundFlight')}
            </h3>
          ) : null}
          <dl className="booking-panel__facts booking-panel__facts--rows">
            {leg.lines.map((line) => (
              <div key={line.type}>
                <dt>
                  {t(TYPE_LABELS[line.type])} × {num(line.count)}
                </dt>
                <dd>{line.unit ? money(line.unit.total * line.count) : '—'}</dd>
              </div>
            ))}
            {leg.flight.fareClass ? (
              <div className="sa-text-muted">
                <dt>{t('common.fareClass')}</dt>
                <dd dir="ltr">{leg.flight.fareClass}</dd>
              </div>
            ) : null}
            {leg.quote && (leg.quote.baggage.weight || leg.quote.baggage.pieces) ? (
              <div className="sa-text-muted">
                <dt>{t('common.baggage')}</dt>
                <dd dir="ltr">
                  {[leg.quote.baggage.weight, leg.quote.baggage.pieces].filter(Boolean).join(' / ')}
                </dd>
              </div>
            ) : null}
            {leg.flight.refundable === false ? (
              <div className="sa-text-muted">
                <dt>{t('common.nonRefundable')}</dt>
                <dd />
              </div>
            ) : null}
          </dl>
          {leg.quote && leg.quote.refundRules.length > 0 ? (
            <details className="sa-text-muted" style={{ marginBlock: '0.75rem' }}>
              <summary>{t('common.refundRules')}</summary>
              <ul dir="ltr" style={{ paddingInlineStart: '1.25rem', marginTop: '0.5rem' }}>
                {leg.quote.refundRules.map((rule, i) => (
                  <li key={i}>
                    {rule.window}: {rule.mode === 'P' ? `${rule.value}%` : money(rule.value)}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </div>
      ))}
      {taxTotal > 0 ? (
        <dl className="booking-panel__facts booking-panel__facts--rows">
          <div className="sa-text-muted">
            <dt>{t('common.taxesIncluded')}</dt>
            <dd>{money(taxTotal)}</dd>
          </div>
        </dl>
      ) : null}
      <div className="booking-panel__total">
        <span>{t('common.totalPayable')}</span>
        <strong>{priced ? money(total) : '—'}</strong>
      </div>
    </section>
  );

  return (
    <main className="sa-page">
      <div className="sa-container">
        <BookingSteps current={phase === 'passengers' ? 1 : phase === 'services' ? 2 : 3} />

        <div className="booking-layout">
          <div className="booking-layout__main">
            {error ? (
              <div className="sa-alert sa-alert--error" role="alert">
                {error}
              </div>
            ) : null}

            {phase === 'services' ? (
              <section className="booking-panel" aria-labelledby="services-title">
                <h2 id="services-title" className="booking-panel__title">
                  {t('common.servicesTitle')}
                </h2>
                <p className="sa-text-muted">{t('common.servicesLead')}</p>
                <div className="services-list">
                  {pricedLegs.map((leg) => (
                    <article key={leg.flight.flightId} className="services-card">
                      <h3>
                        <bdi>
                          {leg.flight.origin} → {leg.flight.destination}
                        </bdi>
                      </h3>
                      <p>
                        {t(`common.${leg.flight.cabin}`, { defaultValue: leg.flight.cabin })}
                        {leg.flight.fareClass ? ` · ${leg.flight.fareClass}` : ''}
                      </p>
                      {leg.quote && (leg.quote.baggage.weight || leg.quote.baggage.pieces) ? (
                        <p>
                          {t('common.baggage')}:{' '}
                          <bdi>
                            {[leg.quote.baggage.weight, leg.quote.baggage.pieces].filter(Boolean).join(' / ')}
                          </bdi>
                        </p>
                      ) : null}
                      <p>
                        {leg.flight.refundable === false
                          ? t('common.nonRefundable')
                          : leg.flight.refundable
                            ? t('common.refundable')
                            : t('common.fareChecking')}
                      </p>
                    </article>
                  ))}
                </div>
                <p className="sa-text-muted">{t('common.servicesCheckinNote')}</p>
                <div className="booking-actions">
                  <Button type="button" variant="ghost" onClick={() => setPhase('passengers')}>
                    {t('common.back')}
                  </Button>
                  <Button type="button" disabled={!priced} onClick={continueFromServices}>
                    {t('common.continueToReview')}
                  </Button>
                </div>
              </section>
            ) : phase === 'passengers' ? (
              <form onSubmit={onContinue} noValidate>
                {passengers.map((p, i) => {
                  const err = (field: string) => errors[`p${i}.${field}`];
                  const id = (field: string) => `pax-${i}-${field}`;
                  return (
                    <section key={i} className="booking-panel booking-panel--form" aria-labelledby={id('title')}>
                      <header className="booking-panel__head">
                        <div className="booking-panel__title-row">
                          <span className="booking-panel__index" aria-hidden="true">
                            {num(i + 1)}
                          </span>
                          <h2 id={id('title')} className="booking-panel__title">
                            {t('common.passengerN', { n: num(i + 1) })}
                            <span className="booking-panel__type">{t(TYPE_LABELS[typeOf(p) ?? 'ADT'])}</span>
                          </h2>
                        </div>
                        <p className="booking-panel__hint">{t('common.nameAsOnDocument')}</p>
                      </header>

                      <div className="booking-panel__body">
                      <div className="booking-panel__choice" role="radiogroup" aria-label={t('common.nationality')}>
                        {(['IR', 'OTHER'] as const).map((value) => (
                          <label key={value} className={p.nationality === value ? 'is-active' : ''}>
                            <input
                              type="radio"
                              name={id('nationality')}
                              value={value}
                              checked={p.nationality === value}
                              onChange={() => updatePassenger(i, 'nationality', value)}
                            />
                            {value === 'IR' ? t('common.iranian') : t('common.nonIranian')}
                          </label>
                        ))}
                      </div>

                      <div className="booking-fields">
                        <p className="booking-fields__label">{t('common.personalDetails')}</p>
                        <div className="sa-grid sa-grid--3">
                        <Field
                          id={id('firstName')}
                          label={t('common.firstNameLatin')}
                          value={p.firstName}
                          onChange={(e) => updatePassenger(i, 'firstName', e.target.value)}
                          autoComplete="given-name"
                          dir="ltr"
                          error={err('firstName')}
                        />
                        <Field
                          id={id('lastName')}
                          label={t('common.lastNameLatin')}
                          value={p.lastName}
                          onChange={(e) => updatePassenger(i, 'lastName', e.target.value)}
                          autoComplete="family-name"
                          dir="ltr"
                          error={err('lastName')}
                        />
                        <div className={`sa-field${err('gender') ? ' has-error' : ''}`}>
                          <label className="sa-label" htmlFor={id('gender')}>
                            {t('common.gender')}
                          </label>
                          <select
                            id={id('gender')}
                            className="sa-input"
                            value={p.gender}
                            onChange={(e) =>
                              updatePassenger(i, 'gender', e.target.value as PassengerForm['gender'])
                            }
                          >
                            <option value="">{t('common.selectPlaceholder')}</option>
                            <option value="M">{t('common.male')}</option>
                            <option value="F">{t('common.female')}</option>
                          </select>
                          {err('gender') ? <p className="field-error">{err('gender')}</p> : null}
                        </div>
                        <JalaliDateSelect
                          id={id('birthDate')}
                          label={t('common.birthDate')}
                          value={p.birthDate}
                          onChange={(iso) => updatePassenger(i, 'birthDate', iso)}
                          years={birthYears}
                          error={err('birthDate')}
                        />
                        </div>
                        <p className="booking-fields__label">{t('common.travelDocument')}</p>
                        <div className="sa-grid sa-grid--3">
                        {p.nationality === 'IR' ? (
                          <Field
                            id={id('nationalCode')}
                            label={t('common.nationalCode')}
                            value={p.nationalCode}
                            onChange={(e) => updatePassenger(i, 'nationalCode', e.target.value)}
                            inputMode="numeric"
                            maxLength={10}
                            dir="ltr"
                            error={err('nationalCode')}
                          />
                        ) : (
                          <Field
                            id={id('passportCountry')}
                            label={t('common.passportCountry')}
                            value={p.passportCountry}
                            onChange={(e) =>
                              updatePassenger(i, 'passportCountry', e.target.value.toUpperCase())
                            }
                            maxLength={2}
                            dir="ltr"
                            error={err('passportCountry')}
                          />
                        )}
                        {needsPassport(p) ? (
                          <>
                            <Field
                              id={id('passportNumber')}
                              label={t('common.passportNumber')}
                              value={p.passportNumber}
                              onChange={(e) => updatePassenger(i, 'passportNumber', e.target.value)}
                              dir="ltr"
                              error={err('passportNumber')}
                            />
                            <JalaliDateSelect
                              id={id('passportExpiry')}
                              label={t('common.passportExpiry')}
                              value={p.passportExpiry}
                              onChange={(iso) => updatePassenger(i, 'passportExpiry', iso)}
                              years={expiryYears}
                              error={err('passportExpiry')}
                            />
                          </>
                        ) : null}
                        </div>
                      </div>
                      </div>
                    </section>
                  );
                })}

                <section className="booking-panel booking-panel--form" aria-labelledby="contact-title">
                  <header className="booking-panel__head">
                    <h2 id="contact-title" className="booking-panel__title">
                      {t('common.contactInfo')}
                    </h2>
                    <p className="booking-panel__hint">{t('common.contactHint')}</p>
                  </header>
                  <div className="booking-panel__body">
                  <div className="sa-grid sa-grid--2">
                    <Field
                      id="contact-mobile"
                      label={t('common.mobile')}
                      value={mobile}
                      onChange={(e) => {
                        setMobile(e.target.value);
                        clearError('mobile');
                      }}
                      inputMode="tel"
                      autoComplete="tel"
                      dir="ltr"
                      error={errors.mobile}
                    />
                    <Field
                      id="contact-email"
                      label={t('common.email')}
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        clearError('email');
                      }}
                      autoComplete="email"
                      dir="ltr"
                      error={errors.email}
                    />
                  </div>
                  </div>
                </section>

                <div className="booking-actions">
                  <Button type="button" variant="ghost" className="booking-actions__back" onClick={() => navigate(-1)}>
                    <span className="booking-actions__icon" aria-hidden="true">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M6 3.5 10.5 8 6 12.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    {t('common.back')}
                  </Button>
                  <Button type="submit" className="booking-actions__next">
                    {t('common.continue')}
                    <span className="booking-actions__icon" aria-hidden="true">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M6 3.5 10.5 8 6 12.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  </Button>
                </div>
              </form>
            ) : (
              <>
                <section className="booking-panel" aria-labelledby="review-pax-title">
                  <h2 id="review-pax-title" className="booking-panel__title">
                    {t('common.passengers')}
                    <button
                      type="button"
                      className="booking-panel__link"
                      onClick={backToPassengers}
                    >
                      {t('common.editPassengers')}
                    </button>
                  </h2>
                  <div className="booking-table-wrap">
                    <table className="sa-table">
                      <thead>
                        <tr>
                          <th>{t('common.fullName')}</th>
                          <th>{t('common.gender')}</th>
                          <th>{t('common.birthDate')}</th>
                          <th>{t('common.document')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {passengers.map((p, i) => (
                          <tr key={i}>
                            <td dir="ltr" style={{ textAlign: 'start' }}>
                              {p.firstName.toUpperCase()} {p.lastName.toUpperCase()}
                            </td>
                            <td>{p.gender === 'M' ? t('common.male') : t('common.female')}</td>
                            <td>{formatIsoDate(p.birthDate, isFa)}</td>
                            <td dir="ltr" style={{ textAlign: 'start' }}>
                              {documentFor(p)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <dl className="booking-panel__facts">
                    <div>
                      <dt>{t('common.mobile')}</dt>
                      <dd dir="ltr">{normalizeMobile(mobile)}</dd>
                    </div>
                    <div>
                      <dt>{t('common.email')}</dt>
                      <dd dir="ltr">{email.trim()}</dd>
                    </div>
                  </dl>
                </section>

                <label className="booking-terms">
                  <input
                    type="checkbox"
                    checked={accepted}
                    onChange={(e) => setAccepted(e.target.checked)}
                  />
                  {t('common.acceptTerms')}
                </label>

                {returnLegFlight ? (
                  <p className="sa-text-muted">{t('common.roundTripNote')}</p>
                ) : null}

                {priceChange ? (
                  <div className="sa-alert" role="alert">
                    {t('common.priceChanged', {
                      old: money(total),
                      amount: money(priceChange.amount),
                    })}
                  </div>
                ) : null}

                <div className="booking-actions">
                  <Button type="button" variant="ghost" onClick={backToServices}>
                    {t('common.back')}
                  </Button>
                  <Button
                    type="button"
                    disabled={!accepted || submitting || !priced}
                    onClick={() => void onPay()}
                  >
                    {submitting
                      ? t('common.loading')
                      : returnLegFlight
                        ? t('common.payOutboundAmount', {
                            amount: money(priceChange?.outboundAmount ?? outboundLeg.total),
                          })
                        : priceChange
                          ? t('common.payNewPrice', { amount: money(priceChange.amount) })
                          : `${t('common.confirmAndPay')} · ${money(total)}`}
                  </Button>
                </div>
                {returnLegFlight ? (
                  <p className="sa-text-muted">{t('common.roundTripPayNote')}</p>
                ) : null}
              </>
            )}
          </div>

          <aside className="booking-layout__aside">
            <FlightSummaryCard
              flight={bookedFlight}
              title={returnLegFlight ? t('common.outboundFlight') : undefined}
            />
            {returnLegFlight ? (
              <FlightSummaryCard flight={returnLegFlight} title={t('common.returnFlight')} />
            ) : null}
            {extraFlights.map((extra, index) => (
              <FlightSummaryCard
                key={extra.flightId}
                flight={extra}
                title={t('common.flightN', { n: num(index + 3) })}
              />
            ))}
            {priceAside}
          </aside>
        </div>
      </div>
    </main>
  );
}
